<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head">
      <h4>{{ text.title }}</h4>
      <p>{{ text.subtitle }}</p>
    </header>

    <div class="app-modal__grid two-columns">
      <label class="app-modal-field">
        <span>{{ text.folio }}</span>
        <input
          v-model.trim="form.folio"
          type="text"
          class="form-control"
          :placeholder="text.placeholders.folio"
        />
      </label>

      <div v-if="showCfdiDate" class="app-modal-field">
        <span>{{ text.cfdiDate }}</span>
        <VueDatePicker
          v-model="form.cfdiDate"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="text.placeholders.date"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="commonText.today"
        />
      </div>

      <div v-if="!hideSaleCustomerFields" class="app-modal-field">
        <span>{{ text.customer }}</span>
        <multiselect
          v-model="form.customer"
          :options="options.customers"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select"
          :placeholder="text.placeholders.customer"
        >
          <template #noResult><span>{{ commonText.noOption }}</span></template>
          <template #noOptions><span>{{ commonText.noOption }}</span></template>
        </multiselect>
      </div>

      <div v-if="!hideSaleCustomerFields" class="app-modal-field">
        <span>{{ text.sale }}</span>
        <multiselect
          v-model="form.sale"
          :options="filteredSalesOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select expense-fee-sale-select"
          :placeholder="text.placeholders.sale"
        >
          <template #noResult><span>{{ commonText.noOption }}</span></template>
          <template #noOptions><span>{{ commonText.noOption }}</span></template>
        </multiselect>
      </div>
    </div>
  </section>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import { resolveSalesStatusCategory } from '@/composables/shared/useSalesStatus';
import { toPositiveOptionId } from '@/utils/optionValues';

export default {
  name: 'ExpenseFeeModalSectionOperational',
  components: {
    VueDatePicker,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    hideSaleCustomerFields: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Object,
      required: true,
    },
    commonText: {
      type: Object,
      required: true,
    },
  },
  computed: {
    showCfdiDate() {
      const voucherType = String(this.form?.voucherType || '').trim().toLowerCase();
      return voucherType === 'cfdi' || voucherType === 'payroll' || voucherType === 'nomina';
    },
    filteredSalesOptions() {
      const options = Array.isArray(this.options?.sales) ? this.options.sales : [];
      const openOptions = options.filter((option) => this.isOpenSaleOption(option));
      const customerId = toPositiveOptionId(this.form?.customer);

      if (customerId <= 0) {
        return openOptions;
      }

      return openOptions.filter((option) => this.resolveSaleCustomerId(option) === customerId);
    },
    selectedSaleCustomerId() {
      return this.resolveSaleCustomerId(this.form?.sale);
    },
  },
  watch: {
    active: {
      immediate: true,
      handler() {
        this.form.expenseStatus = 'pending';
      },
    },
    selectedSaleCustomerId: {
      immediate: true,
      handler(customerId) {
        this.syncCustomerFromSelectedSale(customerId);
      },
    },
    filteredSalesOptions(nextOptions) {
      if (this.hideSaleCustomerFields) {
        return;
      }

      const selectedSaleId = toPositiveOptionId(this.form?.sale);
      if (selectedSaleId <= 0) {
        return;
      }

      const exists = (Array.isArray(nextOptions) ? nextOptions : [])
        .some((option) => toPositiveOptionId(option) === selectedSaleId);

      if (!exists) {
        this.form.sale = null;
      }
    },
  },
  methods: {
    isOpenSaleOption(option) {
      const category = resolveSalesStatusCategory(option?.meta || option || {});
      return category === 'open';
    },
    resolveSaleCustomerId(option) {
      return Number(
        option?.meta?.company_id
        || option?.meta?.customer_id
        || option?.meta?.company?.id
        || option?.meta?.customer?.id
        || option?.company_id
        || option?.customer_id
        || 0
      );
    },
    syncCustomerFromSelectedSale(customerId) {
      const targetCustomerId = Number(customerId || 0);
      if (targetCustomerId <= 0) {
        return;
      }

      const currentCustomerId = toPositiveOptionId(this.form?.customer);
      if (currentCustomerId === targetCustomerId) {
        return;
      }

      const customerOptions = Array.isArray(this.options?.customers) ? this.options.customers : [];
      const matchedCustomer = customerOptions.find((option) => toPositiveOptionId(option) === targetCustomerId);
      if (matchedCustomer) {
        this.form.customer = matchedCustomer;
      }
    },
  },
};
</script>

<style scoped>
.expense-fee-sale-select :deep(.multiselect__single),
.expense-fee-sale-select :deep(.multiselect__placeholder),
.expense-fee-sale-select :deep(.multiselect__option) {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
